/** @prettier */
import { Reducer, useReducer, useEffect } from "react";
import produce from "immer";
import { useHistory } from "react-router-dom";
import {
    TestData,
    getTestReview,
    ReviewQuestionData,
} from "../Run/testRunnerApi";
// import { assertNever } from "../../util/assert";
import { assert, assertNever } from "../../util/assert";
import {
    useQuestionImages,
    ImageLoadActions,
    ImagesLoadState,
} from "../View/useQuestionImages";

const isValidIndex = (number: number, arr: { length: number }) =>
    number >= 0 && number < arr.length;

export const testReviewFilters = [
    "ALL",
    "CORRECT",
    "INCORRECT",
    "UNANSWERED",
    "FLAGGED",
] as const;
export type TestReviewFilter = typeof testReviewFilters[number];

const isValidFilter = (filter?: string): filter is TestReviewFilter => {
    return testReviewFilters.includes(filter as TestReviewFilter);
};

export type ReviewQuestionDataWithImages = ReviewQuestionData & ImagesLoadState;

export type TestReviewLoadedState = {
    state: "loaded";
    questions: ReviewQuestionDataWithImages[];
    // Adding as its own state, so we don't have to refactor away from
    // questions.length in case of paged data
    totalQuestionCount: number;
    currentQuestionIndex: number;
    filterState: TestReviewFilter;
    testName: string;
};
export type TestReviewState =
    | TestReviewLoadedState
    | { state: "loading"; id: string };

type TestReviewSyncAction =
    | {
          event: "testDataLoad";
          id: string;
          testData: TestData;
          questions: ReviewQuestionData[];
          filterState?: TestReviewFilter;
          initialQuestion?: number;
      }
    | {
          event: "selectQuestion";
          index: number | "next" | "prev";
      }
    | { event: "toggleFilter"; filter: TestReviewFilter }
    | ImageLoadActions;
export type TestReviewDispatch = React.Dispatch<TestReviewSyncAction>;

const testStateReducer: Reducer<
    TestReviewState,
    TestReviewSyncAction
> = produce((state: TestReviewState, action: TestReviewSyncAction) => {
    if (action.event === "testDataLoad") {
        // Convert from question number to index.
        // If not specified, index of -1 will fail the isValidIndex check below
        const initialQuestion = (action.initialQuestion ?? 0) - 1;
        const currentQuestionIndex = isValidIndex(
            initialQuestion,
            action.questions,
        )
            ? initialQuestion
            : Math.max(
                  action.questions.findIndex(
                      (q) => !_isFiltered(q, action.filterState!),
                  ),
                  // in the unlikely case that no questions match the filter
                  0,
              );
        return state.state === "loading" && action.id === state.id
            ? ({
                  state: "loaded",
                  questions: action.questions,
                  totalQuestionCount: action.questions.length,
                  currentQuestionIndex,
                  testName: action.testData.name,
                  timeExpired: false,
                  filterState: action.filterState ?? "ALL",
              } as const)
            : state;
    }
    if (state.state === "loading") {
        return state;
    }
    const isNotFiltered = (i: number) => !isFiltered(state, i);
    if (action.event === "toggleFilter") {
        state.filterState =
            state.filterState === action.filter ? "ALL" : action.filter;
        // If the current question is filtered, adjust the index
        if (isFiltered(state, state.currentQuestionIndex)) {
            const indexes = state.questions.map((_, i) => i);
            state.currentQuestionIndex =
                // Find next unfiltered question
                indexes.slice(state.currentQuestionIndex).find(isNotFiltered) ??
                // Or first unfiltered question (if none come after the current question)
                indexes.find(isNotFiltered) ??
                // Shouldn't hit this, unless all questions are filtered somehow
                0;
        }
        return state;
    }
    if (action.event === "selectQuestion") {
        const allIndexes = state.questions.map((_, i) => i);
        const newIndex =
            typeof action.index === "number"
                ? action.index
                : action.index === "next"
                ? // Find next unfiltered question
                  allIndexes
                      .slice(state.currentQuestionIndex + 1)
                      .find(isNotFiltered)
                : // Find previous unfiltered question
                  allIndexes
                      .slice(0, state.currentQuestionIndex)
                      .reverse()
                      .find(isNotFiltered);

        if (newIndex !== undefined) {
            assert(
                isValidIndex(newIndex, state.questions),
                "Attempted to select invalid index",
            );
            state.currentQuestionIndex = newIndex;
        }
        return;
    }
    const question = state.questions.find(({ id }) => id === action.questionId);
    assert(question, `Expected a question with id - ${action.questionId}`);
    if (action.event === "loadingImages") {
        question.images = { state: "loading" };
        return;
    }
    if (action.event === "loadedImages") {
        question.images = {
            state: "loaded",
            stemImages: action.stemImages,
            explanationImages: action.explanationImages,
        };
        return;
    }
    return assertNever(action, `Unhandled action ${action}`);
});

// =========
// Selectors
// =========
export const getCurrentQuestion = (
    state: TestReviewLoadedState,
): ReviewQuestionDataWithImages | undefined => {
    return state.questions[state.currentQuestionIndex];
};

export type QuestionResult = "CORRECT" | "INCORRECT" | "UNANSWERED";
export const questionResult = (
    question: ReviewQuestionData,
): QuestionResult => {
    const { correctAnswer, selectedAnswer, answerChoices } = question;
    if (selectedAnswer === "none") {
        return "UNANSWERED";
    }
    const isCorrect = correctAnswer === answerChoices[selectedAnswer];
    return isCorrect ? "CORRECT" : "INCORRECT";
};

const _isFiltered = (
    question: ReviewQuestionData,
    filter: TestReviewFilter,
) => {
    if (filter === "ALL") {
        return false;
    }
    if (filter === "FLAGGED") {
        return !question.flagged;
    }
    // CORRECT, INCORRECT, UNANSWERED
    return questionResult(question) !== filter;
};
/** Returns true if the question should be disabled based on the current filter state */
export const isFiltered = (state: TestReviewLoadedState, index: number) =>
    _isFiltered(state.questions[index], state.filterState);

export const countForFilter = (
    state: TestReviewLoadedState,
    filter: TestReviewFilter,
) => {
    // Count is number of questions that *aren't* filtered out
    return state.questions.filter((q) => !_isFiltered(q, filter)).length;
};

export const useTestReviewState = (
    testId: string,
    initialFilter?: string,
    initialQuestion?: number,
): [TestReviewState, TestReviewDispatch] => {
    const [state, syncDispatch] = useReducer(testStateReducer, {
        state: "loading",
        id: testId,
    });

    const history = useHistory();

    useEffect(() => {
        const questionPromise = getTestReview(testId);

        questionPromise
            .then(({ questions, ...testData }) => {
                syncDispatch({
                    event: "testDataLoad",
                    id: testId,
                    testData,
                    questions,
                    // Ensure it's a valid filter state
                    filterState: isValidFilter(initialFilter)
                        ? initialFilter
                        : undefined,
                    initialQuestion,
                });
            })
            .catch((e) => {
                console.error("Error reviewing test", e);
                // TODO: toast message?
                history.push(`tests/${testId}/results`);
            });
    }, [testId, syncDispatch, history, initialFilter, initialQuestion]);

    useQuestionImages(state, syncDispatch);

    return [state, syncDispatch];
};
